import { useQueryClient } from '@tanstack/react-query'
import { Download, EllipsisVertical, Pencil, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dropzone } from '@/components/ui/dropzone'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import {
  certificateHolderDownloadHref,
  type COICertificateHolder,
  deleteCertificateHolder,
  sendCOI,
  uploadCertificateHolderDocument,
} from '../../../../models/coi'
import { useCurrentUser } from '../../../../models/user'
import { AddCertificateHolderDialog } from './AddCertificateHolderDialog'

interface CertificateHolderRowActionsProps {
  holder: COICertificateHolder
  orgId?: string
}

export function CertificateHolderRowActions({ holder, orgId }: CertificateHolderRowActionsProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const queryClient = useQueryClient()
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const hasDocument = holder.hasDocument
  const canSend = hasDocument && holder.hasValidEmail
  const sendTooltip = !holder.hasValidEmail ? 'Add an email address first' : 'Send COI'

  function handleEditSuccess() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  async function handleDelete() {
    await deleteCertificateHolder(holder.id, orgId)
    toast.success('Certificate holder deleted')
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  async function handleSendCOI() {
    setSendLoading(true)
    try {
      await sendCOI(holder.id, orgId)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_SENT_LOGS] })
      setSendDialogOpen(false)
      toast.success(`Your COI was sent to ${holder.email}`)
    } catch (error) {
      toast.error(parseError(error))
    } finally {
      setSendLoading(false)
    }
  }

  async function handleUploadDocument() {
    if (!uploadFile) return
    setUploadLoading(true)
    try {
      await uploadCertificateHolderDocument(holder.id, uploadFile, orgId)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
      setUploadDialogOpen(false)
      setUploadFile(null)
      toast.success('COI document uploaded')
    } catch {
      toast.error('Failed to upload COI document')
    } finally {
      setUploadLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button fires no pointer events, so the tooltip needs a
              wrapper to hang off — focusable only while disabled. */}
          <span tabIndex={canSend ? -1 : 0}>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={!canSend}
              // Names what the control does, and stays put when disabled — the
              // reason it is disabled belongs in the tooltip, which the wrapper
              // exposes as the description.
              aria-label="Send COI"
              onClick={() => setSendDialogOpen(true)}
            >
              <Send className="size-4" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{sendTooltip}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon-sm" onClick={() => setEditDialogOpen(true)}>
            <Pencil className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Edit details</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          {hasDocument ? (
            <Button
              variant="ghost"
              size="icon-sm"
              href={certificateHolderDownloadHref(holder.id, orgId)}
              target="_blank"
              aria-label="Download COI"
            >
              <Download className="size-4" />
            </Button>
          ) : (
            <span tabIndex={0}>
              <Button variant="ghost" size="icon-sm" disabled aria-label="Download COI">
                <Download className="size-4" />
              </Button>
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent>Download COI</TooltipContent>
      </Tooltip>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm">
            <EllipsisVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isCaiAdmin && (
            <DropdownMenuItem onClick={() => setUploadDialogOpen(true)}>
              {hasDocument ? 'Replace COI' : 'Upload COI'}
            </DropdownMenuItem>
          )}
          <ConfirmationDialog
            title="Delete certificate holder"
            description="Are you sure you want to delete this certificate holder? This action cannot be undone."
            confirmLabel="Delete"
            onConfirm={handleDelete}
          >
            <DropdownMenuItem onSelect={(e) => e.preventDefault()} variant="destructive">
              Delete certificate holder
            </DropdownMenuItem>
          </ConfirmationDialog>
        </DropdownMenuContent>
      </DropdownMenu>

      <AddCertificateHolderDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
        holder={holder}
        orgId={orgId}
      />

      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(isOpen) => {
          setUploadDialogOpen(isOpen)
          if (!isOpen) setUploadFile(null)
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{hasDocument ? 'Replace COI document' : 'Upload COI document'}</DialogTitle>
          </DialogHeader>
          <Dropzone onChange={setUploadFile} accept=".pdf" maxSizeMB={10} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploadLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadDocument}
              disabled={!uploadFile}
              loading={uploadLoading ? 'Uploading…' : undefined}
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Confirm details to send COI</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div className="flex items-baseline gap-4">
              <p className="text-muted-foreground">Name</p>
              <p className="text-right font-medium">{holder.name}</p>
            </div>
            <div className="flex items-baseline gap-4">
              <p className="text-muted-foreground">Email</p>
              <p className="text-right font-medium">{holder.email}</p>
            </div>
            <div className="flex items-baseline gap-4">
              <p className="text-muted-foreground">Vehicles VIN on COI</p>
              <p className="text-right font-medium">All active vehicles</p>
            </div>
            <div className="flex flex-col items-baseline">
              <p className="text-muted-foreground">Description of operations</p>
              <p className="text-right font-medium">{holder.descriptionOfOperations || '-'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)} disabled={sendLoading}>
              Cancel
            </Button>
            <Button onClick={handleSendCOI} loading={sendLoading ? 'Sending…' : undefined}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
